import moment from "moment";
import { compose } from "redux";
import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import withUserPerm from "services/withUserPerm";

import DatePicker from "components/DatePicker";

import PerformanceSummary from "components/dashboard/PerformanceSummary";
import SpendingAndConversion from "components/dashboard/SpendingAndConversion";

import "styles/Dashboard.css";

import useCurrency from "modules/utils/useCurrency";
import mainActions from "modules/main/actions";
import mainSelectors from "modules/main/selectors";
import formatMetricValue from "modules/utils/formatMetricValue";
import performanceAction from "modules/performance/actions";
import performanceSelectors from "modules/performance/selectors";

import googleActions from "modules/google/actions";
import googleSelectors from "modules/google/selectors";

import Loading from "components/Loading";
import LoadingContainer from "components/LoadingContainer";
import TopBar from "components/TopBar";
import useWindowSize from "layout/useWindowSize";
import styled from "styled-components";

const Dashboard = () => {
  const rmpToken = sessionStorage.getItem("rmp_token");
  const { REACT_APP_RMP_APP_ID } = process.env;
  const [t] = useTranslation();
  const dispatch = useDispatch();
  let history = useHistory();
  const windowSize = useWindowSize();

  const [loading, setLoading] = useState(true);

  const currentAd = useSelector((state) => mainSelectors.currentAd(state));
  const gdashboardList = useSelector((state) => performanceSelectors.gdashboardList(state));
  const [selectedViewValue, setSelectedViewValue] = useState();
  const [yesterdayAsDefault, setYesterdayAsDefault] = useState(false);
  const [loadedSelfServiceSettings, setLoadedSelfServiceSettings] = useState(false);
  const businessInfoResult = useSelector((state) => googleSelectors.getBusinessInfoResult(state));
  const authAdproResult = useSelector((state) => mainSelectors.authAdproResult(state));
  const [showLoadingContainer, setShowLoadingContainer] = useState(false);

  // Get Self Service Settings
  useEffect(() => {
    setShowLoadingContainer(true);

    if (authAdproResult.data.adwords_managed_service !== "Y") {
      dispatch(
        googleActions.getBusinessInfo(rmpToken, {
          appId: REACT_APP_RMP_APP_ID,
        }),
      );
    } else {
      checkAdsAccountConnection();
      setLoadedSelfServiceSettings(true);
    }
  }, [authAdproResult]);

  useEffect(() => {
    setLoadedSelfServiceSettings(true);

    if (businessInfoResult.result === 0 && businessInfoResult.data.adwords_id) {
      dispatch(
        mainActions.SetAd({
          account_name: businessInfoResult.data.adwords_name,
          ads_account_id: businessInfoResult.data.adwords_id,
          type: "g_ads",
          currency_code: "THB",
          rmp_business_id: businessInfoResult.data.business_id,
          create_time: null,
          update_time: null,
        }),
      );

      setShowLoadingContainer(false);
    } else if (businessInfoResult.result === 0 && !businessInfoResult.data.adwords_id) {
      setShowLoadingContainer(false);
      history.push("/no-connected-ads");
    } else if (
      authAdproResult.result === 1 &&
      (authAdproResult.data.adwords_managed_service === "N" || authAdproResult.data.adwords_managed_service === "U")
    ) {
      setLoadedSelfServiceSettings(false);
    }
  }, [businessInfoResult]);

  useEffect(() => {
    if (loadedSelfServiceSettings === true) {
      checkAdsAccountConnection();
    }
  }, [currentAd, loadedSelfServiceSettings]);

  useEffect(() => {
    if (selectedViewValue) {
      dispatch(performanceAction.GetGdashboardList(currentAd.ads_account_id, selectedViewValue.key));
    }
  }, [selectedViewValue, currentAd, dispatch]);

  useEffect(() => {
    setLoading(false);
  }, [gdashboardList]);

  let performanceData = gdashboardList || {};

  if (!yesterdayAsDefault && selectedViewValue && selectedViewValue.key === "today" && performanceData) {
    setYesterdayAsDefault(true);
  }

  const changeDateFilter = (value) => {
    setLoading(true);
    setSelectedViewValue(value);
  };

  const checkAdsAccountConnection = () => {
    if (!currentAd || currentAd === "" || !currentAd.ads_account_id) {
      history.push("/no-connected-ads");
    }
  };

  const isPositiveNumber = (data) => {
    var value = data;
    if (typeof data !== "number") {
      value = data.replace(",", "");
      value = parseFloat(value);
    }
    return value > 0 ? "+" + data : data;
  };

  const performanceInfo = [
    {
      title: t("__clicks"),
      value: performanceData.clicks ? formatMetricValue("click", performanceData.clicks.replace(/\,/g, "")) : "",
      change: !performanceData.subClicks
        ? "-"
        : performanceData.subClicks === "N/A"
          ? "N/A"
          : isPositiveNumber(formatMetricValue("click", performanceData.subClicks)) + "%",
      // icon: "icon-click"
    },
    {
      title: t("__impressions"),
      value: performanceData.impressions
        ? formatMetricValue("impr", performanceData.impressions.replace(/\,/g, ""))
        : "",
      change: !performanceData.subImpressions
        ? "-"
        : performanceData.subImpressions === "N/A"
          ? "N/A"
          : isPositiveNumber(formatMetricValue("impr", performanceData.subImpressions)) + "%",
      // icon: "icon-eye"
    },
    {
      title: t("__ctr"),
      value: formatMetricValue("ctr", performanceData.ctr),
      change: !performanceData.subCtr
        ? "-"
        : performanceData.subCtr === "N/A"
          ? "N/A"
          : isPositiveNumber(formatMetricValue("ctr", performanceData.subCtr)),
      // icon: "icon-ctr"
    },
    {
      title: t("__avg_cpc"),
      value: formatMetricValue("cpc", performanceData.cpc),
      change: !performanceData.subCpc
        ? "-"
        : performanceData.subCpc === "N/A"
          ? "N/A"
          : isPositiveNumber(formatMetricValue("cpc", performanceData.subCpc)) + "%",
      // icon: "icon-click-coins",
      unit: useCurrency(),
    },
  ];

  const spendingConversionInfo = [
    {
      title: t("__dashboard_spending"),
      value: formatMetricValue("cost", performanceData.costFormatted),
      unit: useCurrency(),
      change: !performanceData.subCostFormatted
        ? "-"
        : performanceData.subClicks === "N/A"
          ? "N/A"
          : isPositiveNumber(formatMetricValue("cost", performanceData.subCostFormatted)) + "%",
      // icon: "icon-payment"
    },
    {
      title: t("__dashboard_conversion"),
      value: formatMetricValue("conversion", performanceData.conversion),
      change: !performanceData.subConversion
        ? "-"
        : performanceData.subConversion === "N/A"
          ? "N/A"
          : isPositiveNumber(formatMetricValue("conversion", performanceData.subConversion)) + "%",
      // icon: "icon-retweet"
    },
  ];

  if (loadedSelfServiceSettings === true) {
    return (
      <div>
        <StyledContainerTop useWindowSize={windowSize}>
          <StyledContainerTopBar useWindowSize={windowSize}>
            <TopBar title={t("__dashboard")} />
          </StyledContainerTopBar>
          <DatePicker
            key={moment.locale() + " " + yesterdayAsDefault}
            onSelected={(value) => changeDateFilter(value)}
            defaultIndex={yesterdayAsDefault ? 1 : 0}
          />
        </StyledContainerTop>
        {loading === true ? (
          <Loading />
        ) : (
          <>
            <PerformanceSummary performanceInfo={performanceInfo} />
            <SpendingAndConversion spendingConversionInfo={spendingConversionInfo} />
          </>
        )}
      </div>
    );
  } else {
    return (
      <>
        <LoadingContainer show={showLoadingContainer} top="55" />
      </>
    );
  }
};

export default compose(withUserPerm())(Dashboard);

const StyledContainerTop = styled.div.attrs((props) => ({
  display: props.useWindowSize.width < 835 ? "block" : "flex",
}))`
  display: ${(props) => props.display};
  justify-content: space-between;
  margin-bottom: 24px;
  display: ;
`;

const StyledContainerTopBar = styled.div.attrs((props) => ({
  display: props.useWindowSize.width < 835 ? "none" : "block",
}))`
  display: ${(props) => props.display};
`;
